import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  listLeads,
  uploadLeadsCsv,
  qualifyLead,
  qualifyAll,
  deleteLead,
  deleteAllLeads,
  createLead,
  getSettings,
  getPipelineBrief,
  sendLeadEmail,
  getJob,
  getSystemHealth,
} from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import useVisibilityPoll from "@/hooks/useVisibilityPoll";
import { toast } from "sonner";
import {
  Upload,
  Sparkles,
  Trash2,
  Plus,
  FileSpreadsheet,
  ArrowRight,
  AlertCircle,
  Download,
  Wand2,
  Flame,
  AlertTriangle,
  Rocket,
  CheckCircle2,
  Loader2,
  XCircle,
  Zap,
  LayoutGrid,
  List as ListIcon,
} from "lucide-react";
import AiPipelineBoard from "@/components/AiPipelineBoard";

export default function Leads() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  // View mode toggle — "list" (legacy table) or "pipeline" (AI Pipeline Kanban).
  // Persisted via URL param so deep-links remember choice.
  const viewMode = searchParams.get("view") === "pipeline" ? "pipeline" : "list";
  const setViewMode = (mode) => {
    const next = new URLSearchParams(searchParams);
    if (mode === "pipeline") next.set("view", "pipeline");
    else next.delete("view");
    setSearchParams(next, { replace: true });
  };
  // High-intent mode — honoured deep-link from Dashboard CTAs
  // (/leads?intent=high). When active, the lead list is filtered to
  // leads with composite intent_score >= 80 (or intent_label === 'High'
  // or conversion_score >= 80 or brief.hot_lead_ids membership).
  const highIntentMode = searchParams.get("intent") === "high";
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [qualifying, setQualifying] = useState(null); // id or "all"
  const [hasSettings, setHasSettings] = useState(true);
  const [brief, setBrief] = useState(null);
  const fileRef = useRef(null);
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({
    name: "",
    title: "",
    company: "",
    email: "",
    company_description: "",
  });

  // ============ BURST MODE (bulk async outreach) state ============
  // selectedIds  — checked lead ids
  // burstJobs    — { leadId → { jobId, leadName, status, error } }
  // burstRunning — true while the dispatch+polling loop is active
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [burstJobs, setBurstJobs] = useState({});
  const [burstRunning, setBurstRunning] = useState(false);
  const pollTimerRef = useRef(null);

  // ============ LIVE QUEUE HEALTH ============
  // Polls /api/system/health.async_queue every 30s. Used by the pill in the
  // top-right of the Burst Bar. Null until first fetch completes.
  const [queueHealth, setQueueHealth] = useState(null);

  const refresh = async () => {
    const [data, b] = await Promise.all([
      listLeads(),
      getPipelineBrief().catch(() => null),
    ]);
    setLeads(data);
    setBrief(b);
  };

  useEffect(() => {
    Promise.all([
      listLeads(),
      getSettings(),
      getPipelineBrief().catch(() => null),
    ])
      .then(([l, s, b]) => {
        setLeads(l);
        setHasSettings(!!(s && s.product_name));
        setBrief(b);
      })
      .finally(() => setLoading(false));
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadLeadsCsv(file);
      toast.success(`${res.count} leads imported`);
      await refresh();
    } catch (err) {
      toast.error("Upload failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onQualify = async (id) => {
    if (!hasSettings) {
      toast.error("Configure your product first", {
        description: "Head to Product Setup to calibrate the qualifier.",
      });
      return;
    }
    setQualifying(id);
    try {
      await qualifyLead(id);
      toast.success("Lead qualified");
      await refresh();
    } catch (err) {
      toast.error("Qualification failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setQualifying(null);
    }
  };

  const onQualifyAll = async () => {
    if (!hasSettings) {
      toast.error("Configure your product first");
      return;
    }
    setQualifying("all");
    try {
      const res = await qualifyAll();
      toast.success(`${res.processed} leads qualified`);
      await refresh();
    } catch (err) {
      toast.error("Bulk qualify failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setQualifying(null);
    }
  };

  const onDelete = async (id) => {
    await deleteLead(id);
    await refresh();
    toast.success("Lead removed");
  };

  const onDeleteAll = async () => {
    if (!window.confirm("Delete ALL leads? This cannot be undone.")) return;
    const res = await deleteAllLeads();
    await refresh();
    toast.success(`${res.deleted} leads removed`);
  };

  const onManualAdd = async () => {
    if (!manual.email) {
      toast.error("Email is required");
      return;
    }
    try {
      await createLead(manual);
      setShowManual(false);
      setManual({
        name: "",
        title: "",
        company: "",
        email: "",
        company_description: "",
      });
      await refresh();
      toast.success("Lead added");
    } catch (err) {
      toast.error("Add failed", {
        description: err?.response?.data?.detail || err.message,
      });
    }
  };

  const onLoadDemo = async () => {
    setUploading(true);
    try {
      const res = await fetch("/demo_leads.xlsx");
      const blob = await res.blob();
      const file = new File([blob], "demo_leads.xlsx", {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const up = await uploadLeadsCsv(file);
      toast.success(`${up.count} demo leads loaded`, {
        description: up.leads.map((l) => l.name).join(" · "),
      });
      await refresh();
    } catch (err) {
      toast.error("Demo load failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setUploading(false);
    }
  };

  // Apply base filter (status tab) then layer the high-intent mode on top.
  const baseFiltered =
    filter === "all" ? leads : leads.filter((l) => l.status === filter);
  const hotIds = new Set(brief?.hot_lead_ids || []);
  const isHighIntent = (l) => {
    if ((l.intent_label || "").toLowerCase() === "high") return true;
    if ((l.conversion_score ?? 0) >= 80) return true;
    if (hotIds.has(l.id)) return true;
    if ((l.last_reply_sentiment || "").toLowerCase() === "positive") return true;
    return false;
  };
  const filtered = highIntentMode
    ? baseFiltered.filter(isHighIntent)
    : baseFiltered;

  // --- Burst Mode helpers -----------------------------------------------
  // A lead is eligible for bulk async send when it's QUALIFIED but not yet
  // sent. We intentionally exclude rejected/invalid/sent to match the
  // server-side 403 disqualification guard.
  const isBurstEligible = (l) =>
    l && l.status === "qualified" && (l.final_email || "").trim().length > 0;

  const eligibleInView = filtered.filter(isBurstEligible);
  const eligibleIdsInView = eligibleInView.map((l) => l.id);
  const allEligibleSelected =
    eligibleIdsInView.length > 0 &&
    eligibleIdsInView.every((id) => selectedIds.has(id));

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allEligibleSelected) {
        eligibleIdsInView.forEach((id) => next.delete(id));
      } else {
        eligibleIdsInView.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Aggregate progress from burstJobs — keyed by leadId so state survives
  // selection changes mid-run.
  const burstStats = (() => {
    const values = Object.values(burstJobs);
    const total = values.length;
    let completed = 0, failed = 0, running = 0, queued = 0;
    values.forEach((j) => {
      if (j.status === "completed") completed += 1;
      else if (j.status === "failed") failed += 1;
      else if (j.status === "running") running += 1;
      else queued += 1;
    });
    const pct = total > 0 ? Math.round(((completed + failed) / total) * 100) : 0;
    return { total, completed, failed, running, queued, pct };
  })();

  const pollBurstJobs = useCallback(async (initialJobs) => {
    // initialJobs: { leadId: { jobId, leadName, status } }
    // Poll every 1.2s up to 60s total. Stop when every job is terminal.
    const DEADLINE = Date.now() + 60_000;
    const poll = async () => {
      const entries = Object.entries(initialJobs).filter(
        ([, j]) => j.jobId && j.status !== "completed" && j.status !== "failed",
      );
      if (entries.length === 0 || Date.now() > DEADLINE) {
        setBurstRunning(false);
        // Final refresh so the leads table reflects the new lifecycle/sent_at.
        refresh().catch(() => {});
        return;
      }
      await Promise.all(
        entries.map(async ([leadId, j]) => {
          try {
            const doc = await getJob(j.jobId);
            initialJobs[leadId] = {
              ...j,
              status: doc.status || j.status,
              error: doc.last_error || "",
            };
          } catch (err) {
            // Treat poll failures as transient; keep prior status. Log at
            // debug level so production console stays quiet but devs can
            // trace recurring failures.
            if (typeof console !== "undefined" && console.debug) {
              console.debug("[burst] job poll failed:", j.jobId, err?.response?.status || err?.message);
            }
          }
        }),
      );
      setBurstJobs({ ...initialJobs });
      pollTimerRef.current = setTimeout(poll, 1200);
    };
    await poll();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const runBurstOutreach = async () => {
    const ids = Array.from(selectedIds).filter((id) =>
      leads.some((l) => l.id === id && isBurstEligible(l)),
    );
    if (ids.length === 0) {
      toast.error("Select at least one qualified lead to run burst outreach.");
      return;
    }
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setBurstRunning(true);
    // Seed the progress map so the UI shows N queued slots immediately.
    const initialJobs = {};
    ids.forEach((id) => {
      const l = leads.find((x) => x.id === id);
      initialJobs[id] = {
        jobId: null,
        leadName: l?.name || id,
        status: "dispatching",
      };
    });
    setBurstJobs({ ...initialJobs });

    // Dispatch in parallel. The server validates + enqueues — we just collect job ids.
    await Promise.all(
      ids.map(async (leadId) => {
        try {
          const resp = await sendLeadEmail(leadId, { async_mode: true });
          if (resp?.job_id) {
            initialJobs[leadId] = {
              ...initialJobs[leadId],
              jobId: resp.job_id,
              status: "queued",
            };
          } else if (resp?.status === "success") {
            // Broker was down → server fell back to sync. Mark completed directly.
            initialJobs[leadId] = {
              ...initialJobs[leadId],
              status: "completed",
              error: "",
            };
          } else {
            initialJobs[leadId] = {
              ...initialJobs[leadId],
              status: "failed",
              error: "Dispatch failed",
            };
          }
        } catch (err) {
          initialJobs[leadId] = {
            ...initialJobs[leadId],
            status: "failed",
            error: err?.response?.data?.detail || err.message || "Dispatch error",
          };
        }
      }),
    );
    setBurstJobs({ ...initialJobs });
    toast.success(`Burst started — ${ids.length} leads dispatched`);
    await pollBurstJobs(initialJobs);
  };

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  // Live queue health — poll every 30s, visibility-aware (pauses while
  // the tab is hidden, resumes + immediate refetch on visibilitychange).
  useVisibilityPoll(async () => {
    try {
      const h = await getSystemHealth();
      setQueueHealth(h?.async_queue || null);
    } catch {
      setQueueHealth({ broker_reachable: false, enabled: false });
    }
  }, 30_000);

  const pendingCount = leads.filter((l) => l.status === "pending").length;

  const filters = [
    { key: "all", label: "All", count: leads.length },
    { key: "pending", label: "Pending", count: pendingCount },
    {
      key: "qualified",
      label: "Qualified",
      count: leads.filter((l) => l.status === "qualified").length,
    },
    {
      key: "sent",
      label: "Sent",
      count: leads.filter((l) => l.status === "sent").length,
    },
    {
      key: "rejected",
      label: "Rejected",
      count: leads.filter((l) => l.status === "rejected").length,
    },
    {
      key: "invalid",
      label: "Invalid",
      count: leads.filter((l) => l.status === "invalid").length,
    },
  ];

  return (
    <div className="px-6 md:px-10 py-8 space-y-6" data-testid="leads-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-caps text-[#002FA7]">Pipeline</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Leads
          </h1>
          <p className="mt-1 text-[14px] text-slate-600">
            Upload, qualify, personalize, and send.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            ref={fileRef}
            onChange={onFile}
            accept=".csv,.xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            className="hidden"
            data-testid="csv-file-input"
          />
          <Button
            variant="outline"
            onClick={onLoadDemo}
            disabled={uploading}
            data-testid="load-demo-btn"
            className="h-10 rounded-md border-slate-200"
          >
            <Wand2 className="h-4 w-4 mr-2" />
            Load demo xlsx
          </Button>
          <Button
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="upload-csv-btn"
            className="h-10 rounded-md border-slate-200"
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? "Uploading…" : "Upload CSV / XLSX"}
          </Button>

          <Dialog open={showManual} onOpenChange={setShowManual}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="h-10 rounded-md border-slate-200"
                data-testid="manual-add-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add lead
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="manual-add-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">Add lead manually</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <Input
                  placeholder="Name"
                  value={manual.name}
                  onChange={(e) => setManual({ ...manual, name: e.target.value })}
                  data-testid="manual-name"
                />
                <Input
                  placeholder="Title"
                  value={manual.title}
                  onChange={(e) => setManual({ ...manual, title: e.target.value })}
                  data-testid="manual-title"
                />
                <Input
                  placeholder="Company"
                  value={manual.company}
                  onChange={(e) =>
                    setManual({ ...manual, company: e.target.value })
                  }
                  data-testid="manual-company"
                />
                <Input
                  placeholder="Email *"
                  value={manual.email}
                  onChange={(e) => setManual({ ...manual, email: e.target.value })}
                  data-testid="manual-email"
                />
                <Textarea
                  placeholder="Company description"
                  value={manual.company_description}
                  onChange={(e) =>
                    setManual({
                      ...manual,
                      company_description: e.target.value,
                    })
                  }
                  data-testid="manual-company-description"
                />
              </div>
              <DialogFooter>
                <Button
                  onClick={onManualAdd}
                  className="bg-[#002FA7] hover:bg-[#002072]"
                  data-testid="manual-save-btn"
                >
                  Add lead
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button
            onClick={onQualifyAll}
            disabled={qualifying === "all" || pendingCount === 0}
            className="h-10 rounded-md bg-[#002FA7] hover:bg-[#002072]"
            data-testid="qualify-all-btn"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            {qualifying === "all"
              ? "Running…"
              : `Run outreach engine (${pendingCount})`}
          </Button>
        </div>
      </div>

      {!hasSettings && (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 p-4 text-[13px] text-amber-900 flex items-start gap-3"
          data-testid="setup-warning"
        >
          <AlertCircle className="h-4 w-4 mt-0.5" />
          <div>
            <div className="font-semibold">Set up your product first</div>
            Before you qualify, describe what you sell so the AI can score
            leads.{" "}
            <Link to="/setup" className="underline font-medium">
              Open setup →
            </Link>
          </div>
        </div>
      )}

      {/* Upload drop zone if no leads yet */}
      {leads.length === 0 && !loading && (
        <div
          className="rounded-md border-2 border-dashed border-slate-300 bg-slate-50 p-16 text-center"
          data-testid="csv-dropzone"
        >
          <FileSpreadsheet className="h-8 w-8 text-slate-400 mx-auto" />
          <h3 className="mt-4 font-display text-xl font-bold tracking-tight text-slate-900">
            Drop your leads file here
          </h3>
          <p className="mt-1 text-[13px] text-slate-600 max-w-md mx-auto">
            Accepts <span className="font-mono">.csv</span> or{" "}
            <span className="font-mono">.xlsx</span>. Columns:{" "}
            <span className="font-mono">
              name, title, company, email, company_description
            </span>
          </p>
          <Button
            onClick={() => fileRef.current?.click()}
            className="mt-5 bg-[#002FA7] hover:bg-[#002072]"
            data-testid="dropzone-upload-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Select file
          </Button>
          <div className="mt-3 flex items-center justify-center gap-4 text-[12px]">
            <button
              onClick={onLoadDemo}
              className="text-[#002FA7] hover:underline font-medium"
              data-testid="dropzone-demo-btn"
            >
              <Wand2 className="h-3 w-3 inline mr-1" /> Load 2-lead demo (xlsx)
            </button>
            <a
              href="/demo_leads.xlsx"
              download
              className="text-slate-500 hover:text-slate-800"
              data-testid="dropzone-download-demo"
            >
              <Download className="h-3 w-3 inline mr-1" />
              Download sample XLSX
            </a>
          </div>
        </div>
      )}

      {/* High-Intent Mode banner — activated via /leads?intent=high from
          Dashboard CTAs. Matches the UX promise of "these leads most likely
          to convert today". User can one-click exit back to the full list. */}
      {highIntentMode && leads.length > 0 && (
        <section
          className="rounded-xl border border-orange-200 bg-gradient-to-r from-orange-50 via-white to-amber-50 px-4 py-3 flex items-center gap-3 flex-wrap"
          data-testid="high-intent-mode-banner"
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-orange-100 text-orange-800 px-3 py-1 text-[12px] font-bold border border-orange-200">
            <Flame className="h-3.5 w-3.5" />
            High-Intent Mode
          </span>
          <span className="text-[13px] text-slate-700">
            Showing leads most likely to convert today.
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("intent");
              setSearchParams(next, { replace: true });
            }}
            data-testid="exit-high-intent-mode-btn"
            className="text-[12px] text-slate-600 hover:text-slate-900 font-semibold"
          >
            Show all leads ×
          </button>
        </section>
      )}

      {/* Filters */}
      {leads.length > 0 && (
        <>
          <div className="flex items-center flex-wrap gap-2 border-b border-slate-200">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={`relative px-4 py-2.5 text-[13px] font-medium transition-colors ${
                  filter === f.key
                    ? "text-[#002FA7]"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {f.label}{" "}
                <span className="ml-1 font-mono text-[11px] text-slate-400">
                  {f.count}
                </span>
                {filter === f.key && (
                  <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-[#002FA7]" />
                )}
              </button>
            ))}
            <div className="flex-1" />
            {/* AI Pipeline view toggle — additive Kanban view */}
            <div
              className="inline-flex items-center rounded-md border border-slate-200 bg-white p-0.5"
              data-testid="view-mode-toggle"
              data-active-view={viewMode}
            >
              <button
                type="button"
                onClick={() => setViewMode("list")}
                data-testid="view-mode-list-btn"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11.5px] font-semibold transition-colors ${
                  viewMode === "list"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                title="Classic list view"
              >
                <ListIcon className="h-3.5 w-3.5" />
                List
              </button>
              <button
                type="button"
                onClick={() => setViewMode("pipeline")}
                data-testid="view-mode-pipeline-btn"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11.5px] font-semibold transition-colors ${
                  viewMode === "pipeline"
                    ? "bg-gradient-to-r from-indigo-600 to-violet-600 text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                title="AI Pipeline Kanban — sales-native view"
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                AI Pipeline
              </button>
            </div>
            {leads.length > 0 && (
              <button
                onClick={onDeleteAll}
                className="text-[12px] text-slate-500 hover:text-red-600"
                data-testid="delete-all-btn"
              >
                Clear all
              </button>
            )}
          </div>

          {/* ======== Burst Mode action bar ======== */}
          {(selectedIds.size > 0 || burstStats.total > 0) && (
            <BurstBar
              selectedCount={selectedIds.size}
              stats={burstStats}
              running={burstRunning}
              queueHealth={queueHealth}
              onRun={runBurstOutreach}
              onClear={() => {
                clearSelection();
                setBurstJobs({});
              }}
            />
          )}

          {viewMode === "pipeline" ? (
            <AiPipelineBoard leads={filtered} />
          ) : (
          <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left label-caps border-b border-slate-200">
                  <th className="pl-5 pr-1 py-3 w-[36px]">
                    <input
                      type="checkbox"
                      checked={allEligibleSelected}
                      onChange={toggleSelectAllVisible}
                      disabled={eligibleIdsInView.length === 0 || burstRunning}
                      data-testid="burst-select-all"
                      className="h-4 w-4 rounded border-slate-300 text-[#002FA7] focus:ring-[#002FA7] disabled:opacity-40 cursor-pointer"
                      title={
                        eligibleIdsInView.length === 0
                          ? "No qualified leads with drafted email in this view"
                          : "Select all qualified leads in this view"
                      }
                    />
                  </th>
                  <th className="px-4 py-3">Lead</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">ICP / Conv</th>
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => {
                  const isHot = brief?.hot_lead_ids?.includes(l.id);
                  const isStuck = brief?.stuck_lead_ids?.includes(l.id);
                  const eligible = isBurstEligible(l);
                  const selected = selectedIds.has(l.id);
                  const jobForLead = burstJobs[l.id];
                  return (
                  <tr
                    key={l.id}
                    className={`border-b border-slate-100 hover:bg-slate-50/70 transition-colors ${
                      selected ? "bg-indigo-50/40" : ""
                    }`}
                    data-testid={`lead-row-${l.id}`}
                  >
                    <td className="pl-5 pr-1 py-3 align-top">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleSelect(l.id)}
                        disabled={!eligible || burstRunning}
                        data-testid={`burst-select-${l.id}`}
                        className="h-4 w-4 rounded border-slate-300 text-[#002FA7] focus:ring-[#002FA7] disabled:opacity-30 cursor-pointer"
                        title={
                          eligible
                            ? "Select for burst outreach"
                            : "Only qualified leads with a drafted email can be burst-sent"
                        }
                      />
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium text-slate-900 flex items-center gap-1.5 flex-wrap">
                        {l.name || "—"}
                        {isHot && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200"
                            data-testid={`hot-badge-${l.id}`}
                            title="High intent / fresh positive reply"
                          >
                            <Flame className="h-2.5 w-2.5" />
                            Hot
                          </span>
                        )}
                        {isStuck && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200"
                            data-testid={`stuck-badge-${l.id}`}
                            title="No activity for 3+ days"
                          >
                            <AlertTriangle className="h-2.5 w-2.5" />
                            Stuck
                          </span>
                        )}
                      </div>
                      <div className="text-[11.5px] text-slate-500">
                        {l.title || "—"}
                        {l.email && (
                          <span className="ml-2 font-mono text-slate-400">
                            {l.email}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top text-slate-700">
                      {l.company || "—"}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <IcpCell score={l.icp_score} />
                      {l.conversion_score != null && (
                        <div className="mt-1 text-[11px] text-slate-500 font-mono">
                          conv {l.conversion_score}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      {l.priority ? (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-semibold border ${getPriorityPillClass(
                            l.priority,
                          )}`}
                          data-testid={`priority-${l.id}`}
                        >
                          {l.priority}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <StatusBadge status={l.status} />
                      {jobForLead && (
                        <span
                          className="ml-2 inline-block align-middle"
                          data-testid={`burst-job-chip-${l.id}`}
                          data-job-status={jobForLead.status}
                          title={
                            jobForLead.error
                              ? `Job ${jobForLead.status}: ${jobForLead.error}`
                              : `Job ${jobForLead.status}`
                          }
                        >
                          <BurstJobDot status={jobForLead.status} />
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 align-top text-right space-x-2 whitespace-nowrap">
                      {l.status === "pending" && (
                        <Button
                          size="sm"
                          onClick={() => onQualify(l.id)}
                          disabled={qualifying === l.id}
                          data-testid={`qualify-btn-${l.id}`}
                          className="h-8 bg-[#002FA7] hover:bg-[#002072]"
                        >
                          {qualifying === l.id ? "…" : "Qualify"}
                        </Button>
                      )}
                      <Link to={`/leads/${l.id}`}>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`open-btn-${l.id}`}
                          className="h-8 border-slate-200"
                        >
                          Open <ArrowRight className="h-3 w-3 ml-1" />
                        </Button>
                      </Link>
                      <button
                        onClick={() => onDelete(l.id)}
                        className="text-slate-400 hover:text-red-600 inline-flex items-center align-middle ml-1"
                        data-testid={`delete-btn-${l.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-6 py-12 text-center text-sm text-slate-500"
                    >
                      {highIntentMode ? (
                        <div
                          data-testid="high-intent-empty-state"
                          className="flex flex-col items-center gap-2"
                        >
                          <div className="h-10 w-10 rounded-full bg-orange-50 grid place-items-center">
                            <Flame className="h-5 w-5 text-orange-500 opacity-60" />
                          </div>
                          <div className="text-[13px] font-semibold text-slate-700">
                            No high-intent leads right now.
                          </div>
                          <div className="text-[12px] text-slate-500 max-w-sm">
                            Qualify more leads, upload fresh prospects, or wait
                            for replies to surface new intent signals.
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <Button
                              variant="outline"
                              className="h-8 rounded-md text-[12px]"
                              onClick={() => {
                                const next = new URLSearchParams(searchParams);
                                next.delete("intent");
                                setSearchParams(next, { replace: true });
                              }}
                              data-testid="high-intent-empty-show-all"
                            >
                              Show all leads
                            </Button>
                          </div>
                        </div>
                      ) : (
                        "No leads in this view."
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          )}
        </>
      )}
    </div>
  );
}

const PRIORITY_PILL_CLASS = {
  High: "bg-red-50 text-red-700 border-red-200",
  Medium: "bg-amber-50 text-amber-800 border-amber-200",
  Low: "bg-slate-100 text-slate-700 border-slate-200",
};
const getPriorityPillClass = (priority) =>
  PRIORITY_PILL_CLASS[priority] || PRIORITY_PILL_CLASS.Low;

const getIcpColor = (score) => {
  if (score >= 80) return "#16A34A";
  if (score >= 60) return "#002FA7";
  return "#DC2626";
};

function IcpCell({ score }) {
  if (score == null) return <span className="text-slate-400">—</span>;
  const color = getIcpColor(score);
  return (
    <div className="flex items-center gap-2">
      <span
        className="font-mono font-semibold text-[13px]"
        style={{ color }}
      >
        {score}
      </span>
      <div className="h-1 w-16 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full"
          style={{ width: `${score}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

// ============ Burst Mode UI components ============

function BurstBar({ selectedCount, stats, running, queueHealth, onRun, onClear }) {
  const { total, completed, failed, running: inFlight, queued, pct } = stats;
  const allDone =
    total > 0 && (completed + failed) === total && !running;
  return (
    <div
      data-testid="burst-bar"
      className="rounded-md border border-indigo-200 bg-gradient-to-r from-indigo-50 via-white to-emerald-50 p-4 flex flex-col gap-3"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 text-[12px] font-semibold text-indigo-900">
          <Rocket className="h-4 w-4" />
          Burst Mode
        </div>
        {running && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 text-[11px] font-semibold tracking-wide"
            data-testid="burst-parallel-label"
            title="Jobs are executing in parallel across Celery workers"
          >
            <Zap className="h-3 w-3" />
            Running in parallel
          </span>
        )}
        {total === 0 ? (
          <span
            className="text-[13px] text-slate-600"
            data-testid="burst-bar-selected-count"
          >
            {selectedCount} qualified {selectedCount === 1 ? "lead" : "leads"} selected
          </span>
        ) : (
          <span
            className="text-[13px] text-slate-700 font-mono"
            data-testid="burst-bar-summary"
          >
            {completed}/{total} completed
            {failed > 0 && (
              <span className="text-red-600"> · {failed} failed</span>
            )}
            {inFlight > 0 && (
              <span className="text-amber-700"> · {inFlight} running</span>
            )}
            {queued > 0 && !running && (
              <span className="text-slate-500"> · {queued} queued</span>
            )}
            {running && queued > 0 && (
              <span className="text-indigo-600"> · dispatching…</span>
            )}
          </span>
        )}
        <div className="flex-1" />
        <QueueHealthPill health={queueHealth} />
        {total > 0 && !running && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            data-testid="burst-clear-btn"
            className="h-8 text-[12px] text-slate-500 hover:text-slate-800"
          >
            Clear
          </Button>
        )}
        <Button
          onClick={onRun}
          disabled={selectedCount === 0 || running}
          data-testid="burst-run-btn"
          className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md"
        >
          {running ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Running…
            </>
          ) : (
            <>
              <Rocket className="h-4 w-4 mr-2" />
              Run AI Outreach
            </>
          )}
        </Button>
      </div>
      {total > 0 && (
        <div
          className="h-2 bg-slate-100 rounded-full overflow-hidden flex"
          data-testid="burst-progress-bar"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div
            className="h-full bg-emerald-500 transition-all"
            style={{ width: `${total === 0 ? 0 : (completed / total) * 100}%` }}
            data-testid="burst-progress-completed"
          />
          <div
            className="h-full bg-red-500 transition-all"
            style={{ width: `${total === 0 ? 0 : (failed / total) * 100}%` }}
            data-testid="burst-progress-failed"
          />
          <div
            className="h-full bg-amber-400 animate-pulse"
            style={{ width: `${total === 0 ? 0 : (inFlight / total) * 100}%` }}
            data-testid="burst-progress-running"
          />
        </div>
      )}
      {allDone && (
        <div
          className="text-[12px] text-emerald-700 font-medium"
          data-testid="burst-bar-done-message"
        >
          {failed === 0
            ? `All ${total} leads dispatched successfully.`
            : `${completed} succeeded · ${failed} failed. Check the status dots below.`}
        </div>
      )}
    </div>
  );
}

function BurstJobDot({ status }) {
  if (status === "completed") {
    return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />;
  }
  if (status === "failed") {
    return <XCircle className="h-3.5 w-3.5 text-red-600" />;
  }
  if (status === "running" || status === "dispatching") {
    return (
      <Loader2 className="h-3.5 w-3.5 text-amber-600 animate-spin" />
    );
  }
  // queued
  return (
    <span className="inline-block h-2 w-2 rounded-full bg-indigo-400 animate-pulse" />
  );
}

// ============ Live Queue Health pill ============
// Colour rules:
//   green  → broker_reachable=true AND total queue backlog <= 20
//   amber  → broker_reachable=true AND total queue backlog > 20
//   red    → broker_reachable=false OR async_queue.enabled=false
//   slate  → null (not fetched yet)
function QueueHealthPill({ health }) {
  if (!health) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-medium border border-slate-200"
        data-testid="queue-health-pill"
        data-health-status="loading"
      >
        <span className="h-2 w-2 rounded-full bg-slate-300" />
        Queue…
      </span>
    );
  }
  const reachable = !!health.broker_reachable;
  const enabled = !!health.enabled;
  const pending = health.pending || {};
  const backlog =
    typeof health.total_pending === "number"
      ? health.total_pending
      : Object.values(pending).reduce((a, b) => a + Number(b || 0), 0);

  let status = "green";
  let label = "Queue healthy";
  let title = `Broker reachable · backlog ${backlog} · failed ${health.failed_jobs_total || 0}`;
  if (!reachable || !enabled) {
    status = "red";
    label = enabled ? "Broker down" : "Queue disabled";
    title = enabled
      ? "Redis broker is unreachable — sends will fall back to sync"
      : "Async queue is disabled by config (ASYNC_QUEUE_ENABLED=false)";
  } else if (backlog > 20) {
    status = "amber";
    label = `Backlog ${backlog}`;
    title = `Workers are catching up — ${backlog} jobs queued`;
  }

  const tone = {
    green: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
  }[status];
  const dot = {
    green: "bg-emerald-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
  }[status];

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border ${tone}`}
      data-testid="queue-health-pill"
      data-health-status={status}
      data-backlog={backlog}
      title={title}
    >
      <span className={`h-2 w-2 rounded-full ${dot} ${status !== "red" ? "animate-pulse" : ""}`} />
      {label}
    </span>
  );
}
